import { useEffect, useMemo } from "react";
import { Box, Dialog, DialogContent, IconButton, Stack, TextField, Theme, Typography } from "@mui/material";
import { Controller, Resolver, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Button as FoodsButton, FoodsIcon } from "@foodpilot/foods";
import { useSnackbar } from "src/utils/useSnackbar";
import { DataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import {
    DataListValueOverrideWrite,
    useUpsertDataListValueOverride,
} from "src/api/client-api/dataListValueOverride/useUpsertDataListValueOverride";
import { DataListAutocomplete } from "src/components/Selector/DataListSelector/DataListAutocomplete";
import { DataListVersionSelect } from "src/components/Selector/DataListSelector/DataListVersionSelect";
import { DataListOption, DataListSchema, DataListVersionSchema } from "src/api/sure-api/dataList/useGetAllDataList";
import { useGetOneDataList } from "src/api/sure-api/dataList/useGetOneDataList";
import { useGetOneDataListVersion } from "src/api/sure-api/dataList/useGetOneDataListVersion";
import { DataListOptionAutocomplete } from "src/components/Selector/DataListSelector/DataListOptionAutocomplete";
import { DataListValueOverrideFields } from "./DataListValueOverrideFields";

// Zod schema for DataListValueOverride form validation
const DataListOptionValues = z.object({
    type: z.string(),
    context: z.record(z.string(), z.string().or(z.number())).refine((context) => context.id !== undefined, {
        message: "Context must contain the entity ID",
    }),
});

const DataListOptionValueOverrideFormSchema = DataListOptionValues.extend({
    id: z.number().optional(),
    "@id": z.string().optional(),
    dataListOptionCode: z.string().optional(),
    dataListOptionId: z.number(),
});

const DataListExtraOptionFormSchema = DataListOptionValues.extend({
    id: z.number().optional(),
    "@id": z.string().optional(),
    code: z.string().trim().min(1, "Code is required"),
    label: z.string().trim().min(1, "Label is required"),
});

const DataListValueOverrideFormSchema = z.object({
    id: z.number().optional(),
    dataList: DataListSchema,
    dataListVersion: DataListVersionSchema.optional(),
    dataListOptionValueOverrides: DataListOptionValueOverrideFormSchema.array(),
    dataListExtraOptions: DataListExtraOptionFormSchema.array(),
});

export type DataListOptionValueOverrideForm = z.infer<typeof DataListOptionValueOverrideFormSchema>;
export type DataListExtraOptionForm = z.infer<typeof DataListExtraOptionFormSchema>;
export type DataListValueOverrideFormData = z.infer<typeof DataListValueOverrideFormSchema>;

const normalizeDataListExtraOptionCode = (code: string): string => {
    return code.trim().replace(/^extra:/i, "");
};

const dataListExtraOptionTextFieldSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "24px",
        minHeight: "40px",
        backgroundColor: "white",
        "& fieldset": {
            borderColor: "#E7E6E4",
        },
        "&:hover fieldset": {
            borderColor: "#D0D0D0",
        },
        "&.Mui-focused fieldset": {
            borderColor: (theme: Theme) => theme.palette.primary[600],
        },
    },
    "& .MuiInputBase-input": {
        fontSize: "0.875rem",
        padding: "0px 16px !important",
        color: "#393A36",
        "&::placeholder": {
            color: "#A3A2A0",
            opacity: 1,
        },
    },
};

type DataListValueOverrideModalProps = {
    open: boolean;
    onClose: () => void;
    override?: DataListValueOverride;
    onSuccess?: (override: DataListValueOverride) => void;
};

export const DataListValueOverrideModal = (props: DataListValueOverrideModalProps) => {
    const { open, onClose, override, onSuccess } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const mutation = useUpsertDataListValueOverride();

    const { dataListVersion: loadedDataListVersion } = useGetOneDataListVersion(override?.dataListVersionId, {
        enabled: !!override?.dataListVersionId && open,
    });

    const { dataList: loadedDataListForEdit } = useGetOneDataList(loadedDataListVersion?.dataList.id, {
        enabled: !!override && !!loadedDataListVersion?.dataList.id && open,
    });

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
        watch,
        getValues,
        setValue,
    } = useForm<DataListValueOverrideFormData>({
        mode: "onChange",
        resolver: zodResolver(DataListValueOverrideFormSchema) as Resolver<DataListValueOverrideFormData>,
        defaultValues: {
            id: undefined,
            dataList: undefined,
            dataListVersion: undefined,
            dataListOptionValueOverrides: [],
            dataListExtraOptions: [],
        },
    });

    const watchedDataList = watch("dataList");
    const watchedDataListVersion = watch("dataListVersion");
    const watchedOptionOverrides = watch("dataListOptionValueOverrides");

    const { dataList: loadedDataListForCreate } = useGetOneDataList(watchedDataList?.id, {
        enabled: !override && !!watchedDataList?.id && open,
    });

    const effectiveDataList = override ? loadedDataListForEdit : (loadedDataListForCreate ?? watchedDataList);
    const watchedDataListOptions = useMemo(() => watchedDataListVersion?.options ?? [], [watchedDataListVersion]);
    const watchedDataListVersions = useMemo(() => effectiveDataList?.versions ?? [], [effectiveDataList]);

    useEffect(() => {
        if (override || !loadedDataListForCreate || watchedDataList?.id !== loadedDataListForCreate.id) {
            return;
        }

        setValue("dataList", loadedDataListForCreate);
    }, [override, loadedDataListForCreate, watchedDataList?.id, setValue]);

    useEffect(() => {
        if (!open) return;

        if (override) {
            if (!loadedDataListForEdit || !loadedDataListVersion) return;

            reset({
                id: override.id,
                dataList: loadedDataListForEdit,
                dataListVersion: loadedDataListVersion,
                dataListOptionValueOverrides: override.dataListOptionValueOverrides.map((optionOverride) => ({
                    ...optionOverride,
                    dataListOptionId:
                        loadedDataListVersion.options.find(
                            (option) => option.code === optionOverride.dataListOptionCode,
                        )?.id ?? 0,
                })),
                dataListExtraOptions: override.dataListExtraOptions,
            });
        } else {
            reset({
                id: undefined,
                dataList: undefined,
                dataListVersion: undefined,
                dataListOptionValueOverrides: [],
                dataListExtraOptions: [],
            });
        }
    }, [open, override, reset, loadedDataListForEdit, loadedDataListVersion]);

    const onSubmitHandler: SubmitHandler<DataListValueOverrideFormData> = (data) => {
        if (!data.dataListVersion) return;

        const payload: DataListValueOverrideWrite = {
            id: override?.id,
            dataListVersionId: data.dataListVersion.id,
            dataListOptionValueOverrides: data.dataListOptionValueOverrides.map((optionOverride) => {
                const { dataListOptionId: _, ...payloadOptionOverride } = optionOverride;

                return {
                    ...payloadOptionOverride,
                    dataListOptionCode:
                        optionOverride.dataListOptionCode?.trim() ||
                        data.dataListVersion?.options.find((option) => option.id === optionOverride.dataListOptionId)
                            ?.code ||
                        "",
                };
            }),
            dataListExtraOptions: data.dataListExtraOptions.map((extraOption) => ({
                ...extraOption,
                code: normalizeDataListExtraOptionCode(extraOption.code),
                label: extraOption.label.trim(),
            })),
        };

        mutation.mutate(payload, {
            onSuccess: (response) => {
                const saved = response.data as DataListValueOverride;
                if (onSuccess) onSuccess(saved);
                handleClose();
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    message: t("settings.data_lists_overrides.error.save_failed", "Failed to save. Please try again."),
                    type: "error",
                });
            },
        });
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh" } }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px 32px 0px 32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                        {override ?
                            t("settings.data_lists_overrides.edit", "Edit data list values overrides")
                        :   t("settings.data_lists_overrides.create", "Create data list values overrides")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ padding: "8px", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
                >
                    <FoodsIcon icon="close" size={1.5} />
                </IconButton>
            </Box>

            <DialogContent sx={{ padding: "32px", backgroundColor: "#FEFCFB", overflowY: "auto" }}>
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing="24px">
                        <Box>
                            <Stack spacing="24px">
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.data_lists_overrides.data_list", "Data list")}* :
                                    </Typography>
                                    <Controller
                                        name="dataList"
                                        control={control}
                                        render={({ field }) => (
                                            <DataListAutocomplete
                                                value={field.value ?? null}
                                                onChange={(option) => {
                                                    field.onChange(option);

                                                    setValue("dataListVersion", undefined);
                                                    setValue("dataListOptionValueOverrides", []);
                                                }}
                                                placeholder={t(
                                                    "settings.data_lists_overrides.search.data_list",
                                                    "Search for a data list",
                                                )}
                                                error={!!errors.dataList}
                                                helperText={errors.dataList?.message}
                                                disabled={!!override}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("settings.data_lists_overrides.data_list.version", "Data list version")}* :
                                    </Typography>
                                    <Controller
                                        name="dataListVersion"
                                        control={control}
                                        render={({ field }) => (
                                            <DataListVersionSelect
                                                options={watchedDataListVersions}
                                                value={field.value ?? null}
                                                onChange={(option) => {
                                                    field.onChange(option);

                                                    const selectedOptionIds = new Set(
                                                        option?.options.map((dataListOption) => dataListOption.id) ??
                                                            [],
                                                    );
                                                    setValue(
                                                        "dataListOptionValueOverrides",
                                                        getValues("dataListOptionValueOverrides").filter(
                                                            (optionOverride) =>
                                                                selectedOptionIds.has(optionOverride.dataListOptionId),
                                                        ),
                                                    );
                                                }}
                                                disabled={!effectiveDataList || !!override}
                                                error={!!errors.dataListVersion}
                                                helperText={errors.dataListVersion?.message}
                                            />
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t(
                                            "settings.data_lists_overrides.data_list.option.value_overrides",
                                            "Option value overrides",
                                        )}{" "}
                                        :
                                    </Typography>
                                    <DataListValueOverrideFields
                                        property="dataListOptionValueOverrides"
                                        control={control}
                                        watch={watch}
                                        defaultValue={{
                                            dataListOptionCode: "",
                                            dataListOptionId: 0,
                                            type: "entity_score",
                                            context: {},
                                        }}
                                        getAdditionalFields={(index: number) => (
                                            <Stack sx={{ width: "50%" }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        lineHeight: "1.6em",
                                                        color: "#393A36",
                                                        marginBottom: "8px",
                                                    }}
                                                >
                                                    {t(
                                                        "settings.data_lists_overrides.data_list.option",
                                                        "Data list option",
                                                    )}
                                                    * :
                                                </Typography>
                                                <Controller
                                                    name={`dataListOptionValueOverrides.${index}.dataListOptionId`}
                                                    control={control}
                                                    render={({ field: dataListOptionIdField }) => {
                                                        const value =
                                                            watchedDataListOptions.find(
                                                                (option) => option.id === dataListOptionIdField.value,
                                                            ) ?? null;

                                                        const filteredDataListOptions = watchedDataListOptions.filter(
                                                            (dataListOption) => {
                                                                if (dataListOption.id === dataListOptionIdField.value)
                                                                    return true;

                                                                return !watchedOptionOverrides?.some(
                                                                    (override, i) =>
                                                                        i !== index &&
                                                                        override.dataListOptionId !== null &&
                                                                        override.dataListOptionId === dataListOption.id,
                                                                );
                                                            },
                                                        );

                                                        return (
                                                            <DataListOptionAutocomplete
                                                                options={filteredDataListOptions}
                                                                value={value}
                                                                onChange={(option: DataListOption | null) => {
                                                                    setValue(
                                                                        `dataListOptionValueOverrides.${index}.dataListOptionCode`,
                                                                        option?.code ?? "",
                                                                    );
                                                                    dataListOptionIdField.onChange(option?.id ?? null);
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </Stack>
                                        )}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t(
                                            "settings.data_lists_overrides.data_list.option.additional_options",
                                            "Additional data list options",
                                        )}{" "}
                                        :
                                    </Typography>
                                    <DataListValueOverrideFields
                                        property="dataListExtraOptions"
                                        control={control}
                                        watch={watch}
                                        defaultValue={{
                                            code: "",
                                            label: "",
                                            type: "entity_score",
                                            context: {},
                                        }}
                                        getAdditionalFields={(index: number) => (
                                            <Stack direction="row" gap="16px">
                                                <Stack sx={{ width: "50%" }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                            marginBottom: "8px",
                                                        }}
                                                    >
                                                        {t(
                                                            "settings.data_lists_overrides.data_list.option.code",
                                                            "Data list option code",
                                                        )}
                                                        * :
                                                    </Typography>
                                                    <Controller
                                                        name={`dataListExtraOptions.${index}.code`}
                                                        control={control}
                                                        render={({ field: codeField }) => (
                                                            <TextField
                                                                {...codeField}
                                                                fullWidth
                                                                error={!!errors.dataListExtraOptions?.[index]?.code}
                                                                helperText={
                                                                    errors.dataListExtraOptions?.[index]?.code?.message
                                                                }
                                                                placeholder={t(
                                                                    "settings.data_lists_overrides.data_list.option.code.placeholder",
                                                                    "Enter option code",
                                                                )}
                                                                sx={dataListExtraOptionTextFieldSx}
                                                            />
                                                        )}
                                                    />
                                                </Stack>
                                                <Stack sx={{ width: "50%" }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            lineHeight: "1.6em",
                                                            color: "#393A36",
                                                            marginBottom: "8px",
                                                        }}
                                                    >
                                                        {t(
                                                            "settings.data_lists_overrides.data_list.option.label",
                                                            "Data list option label",
                                                        )}
                                                        * :
                                                    </Typography>
                                                    <Controller
                                                        name={`dataListExtraOptions.${index}.label`}
                                                        control={control}
                                                        render={({ field: labelField }) => (
                                                            <TextField
                                                                {...labelField}
                                                                fullWidth
                                                                error={!!errors.dataListExtraOptions?.[index]?.label}
                                                                helperText={
                                                                    errors.dataListExtraOptions?.[index]?.label?.message
                                                                }
                                                                placeholder={t(
                                                                    "settings.data_lists_overrides.data_list.option.label.placeholder",
                                                                    "Enter option label",
                                                                )}
                                                                sx={dataListExtraOptionTextFieldSx}
                                                            />
                                                        )}
                                                    />
                                                </Stack>
                                            </Stack>
                                        )}
                                    />
                                </Box>
                            </Stack>
                        </Box>
                    </Stack>
                </form>
            </DialogContent>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                    padding: "32px",
                    borderTop: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <FoodsButton
                    variant="outlined"
                    onClick={handleClose}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderColor: theme.palette.primary[600],
                        color: theme.palette.primary[600],
                        backgroundColor: "white",
                        "&:hover": {
                            borderColor: theme.palette.primary[600],
                            backgroundColor: "rgba(47, 203, 212, 0.04)",
                        },
                    })}
                >
                    {t("cancel", "Cancel")}
                </FoodsButton>
                <FoodsButton
                    variant="contained"
                    type="submit"
                    onClick={handleSubmit(onSubmitHandler)}
                    disabled={!isValid || !watchedDataListVersion || mutation.isLoading}
                    sx={(theme) => ({
                        borderRadius: "44px",
                        padding: "0px 20px",
                        height: "40px",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                            color: "white !important",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            opacity: 0.36,
                            color: "white !important",
                        },
                    })}
                >
                    {t("save", "Save")}
                </FoodsButton>
            </Box>
        </Dialog>
    );
};
